import getTicketTypesHiddenByRules from './getTicketTypesHiddenByRules';

const INACTIVE_STATUSES = new Set(['disabled', 'archived']);

/**
 * The page's ticket types hidden by rules, given the checkout form's ticket
 * state `{ [groupId]: [{ ticketTypeId, quantity }] }`.
 */
export function getRuleHiddenTicketTypeIds(pageTicketGroups, formTicketGroups) {
  // The store never loads inactive groups, so the dashboard preview leaves them
  // out too and a source in one is missing in both.
  const ticketTypes = (pageTicketGroups ?? [])
    .filter(group => !INACTIVE_STATUSES.has(group.status))
    .flatMap(group => group.ticketTypes ?? []);

  const selectedQuantities = {};
  Object.values(formTicketGroups ?? {})
    .flat()
    .forEach(entry => {
      if (entry?.ticketTypeId == null) return;
      const id = String(entry.ticketTypeId);
      selectedQuantities[id] = (selectedQuantities[id] ?? 0) + (Number(entry.quantity) || 0);
    });

  return getTicketTypesHiddenByRules({ ticketTypes, selectedQuantities });
}

/**
 * Drops every rule-hidden ticket out of the form's ticket state: quantity 0 and
 * no typed pay-what-you-want amount, as a deselect leaves it. One pass is enough
 * because the evaluator already treats a hidden source as unselected.
 *
 * Returns `formTicketGroups` itself when nothing changes, so an unchanged
 * selection never reads as a form change.
 */
export default function pruneRuleHiddenTickets(formTicketGroups, pageTicketGroups) {
  if (!formTicketGroups || typeof formTicketGroups !== 'object') return formTicketGroups;

  const hiddenIds = getRuleHiddenTicketTypeIds(pageTicketGroups, formTicketGroups);
  if (!hiddenIds.size) return formTicketGroups;

  const isPruned = entry =>
    hiddenIds.has(String(entry?.ticketTypeId)) &&
    (Number(entry.quantity) > 0 || entry.payWhatYouWantAmount !== undefined);

  let changed = false;
  const next = {};

  Object.entries(formTicketGroups).forEach(([groupId, entries]) => {
    if (!Array.isArray(entries) || !entries.some(isPruned)) {
      next[groupId] = entries;
      return;
    }

    changed = true;
    next[groupId] = entries.map(entry => {
      if (!isPruned(entry)) return entry;
      const { payWhatYouWantAmount, ...rest } = entry;
      return { ...rest, quantity: 0 };
    });
  });

  return changed ? next : formTicketGroups;
}
